import type { MessageBundle } from "@/i18n/messageBundles"
import { WindowType } from "./types"
import type { GlyphName } from "@/components/desktop/ui"

export interface WindowRegistryMetadata {
    bundles: readonly MessageBundle[]
    titleKey: WindowType
    icon?: { iconSrc?: string; fallbackIcon?: GlyphName }
    homeApp: WindowType
    islandFit: "lane" | "clear"
    isRecord: boolean
    identityOf?: (
        data: Record<string, unknown> | undefined
    ) => string | undefined
    shouldUpdateTitleOnReveal?: boolean
    shouldUpdateDataOnReveal?: boolean
    transientDataKeys?: readonly string[]
    shouldPrefetch: boolean
    isCartageTeamOnly: boolean
    isOffwhite: boolean
    isSelfTitled: boolean
}

function isRecordWithId(value: unknown): value is { id: string } {
    return (
        typeof value === "object" &&
        value !== null &&
        typeof (value as Record<string, unknown>).id === "string"
    )
}

function knowledgeItemIdOf(
    data: Record<string, unknown> | undefined
): string | undefined {
    const item = data?.doc ?? data?.note
    return isRecordWithId(item) ? item.id : undefined
}

function templateIdentityOf(
    data: Record<string, unknown> | undefined
): string | undefined {
    if (!data) return undefined
    if (data.kind === "email" && typeof data.templateKey === "string") {
        return `email:${data.templateKey}`
    }
    if (
        (data.kind === "pdf" || !("kind" in data)) &&
        typeof data.docType === "string"
    ) {
        return `pdf:${data.docType}`
    }
    return undefined
}

export const WINDOW_REGISTRY_METADATA: Record<
    WindowType,
    WindowRegistryMetadata
> = {
    wilson: {
        bundles: [],
        titleKey: "wilson",
        icon: { iconSrc: "/desktop/app-wilson.png" },
        homeApp: "wilson",
        islandFit: "clear",
        isRecord: false,
        shouldPrefetch: false,
        isCartageTeamOnly: false,
        isOffwhite: false,
        isSelfTitled: false,
    },
    comms: {
        bundles: ["comms"],
        titleKey: "comms",
        icon: { iconSrc: "/desktop/app-mail.png" },
        homeApp: "comms",
        islandFit: "lane",
        isRecord: false,
        shouldUpdateDataOnReveal: true,
        shouldPrefetch: true,
        isCartageTeamOnly: false,
        isOffwhite: false,
        isSelfTitled: false,
    },
    settings: {
        bundles: ["settings", "document"],
        titleKey: "settings",
        icon: { iconSrc: "/desktop/app-settings.png" },
        homeApp: "settings",
        islandFit: "lane",
        isRecord: false,
        shouldPrefetch: false,
        isCartageTeamOnly: false,
        isOffwhite: false,
        isSelfTitled: false,
        shouldUpdateDataOnReveal: true,
    },
    explorer: {
        bundles: ["explorer", "settings", "document", "knowledgeFiles"],
        titleKey: "explorer",
        icon: { iconSrc: "/desktop/app-explorer.png" },
        homeApp: "explorer",
        islandFit: "lane",
        isRecord: false,
        shouldUpdateDataOnReveal: true,
        shouldPrefetch: false,
        isCartageTeamOnly: false,
        isOffwhite: false,
        isSelfTitled: true,
    },
    document: {
        bundles: ["document", "explorer", "knowledgeFiles"],
        titleKey: "document",
        homeApp: "explorer",
        islandFit: "clear",
        isRecord: true,
        identityOf: data =>
            typeof data?.documentId === "string"
                ? data.documentId
                : isRecordWithId(data?.document)
                  ? data.document.id
                  : undefined,
        shouldUpdateDataOnReveal: true,
        shouldPrefetch: true,
        isCartageTeamOnly: false,
        isOffwhite: true,
        isSelfTitled: false,
    },
    tasks: {
        bundles: ["tasks", "document"],
        titleKey: "tasks",
        homeApp: "todos",
        islandFit: "clear",
        isRecord: false,
        shouldPrefetch: false,
        isCartageTeamOnly: false,
        isOffwhite: true,
        isSelfTitled: false,
    },
    todos: {
        bundles: ["tasks", "document"],
        titleKey: "todos",
        icon: { iconSrc: "/desktop/app-routines.svg" },
        homeApp: "todos",
        islandFit: "clear",
        isRecord: false,
        shouldPrefetch: true,
        isCartageTeamOnly: false,
        isOffwhite: true,
        isSelfTitled: false,
        shouldUpdateDataOnReveal: true,
    },
    setup: {
        bundles: ["setup", "cartagePay"],
        titleKey: "setup",
        icon: { iconSrc: "/desktop/app-setup.png" },
        homeApp: "setup",
        islandFit: "clear",
        isRecord: false,
        shouldPrefetch: false,
        isCartageTeamOnly: false,
        isOffwhite: true,
        isSelfTitled: false,
    },
    onboarding: {
        bundles: ["onboarding"],
        titleKey: "onboarding",
        homeApp: "setup",
        islandFit: "clear",
        isRecord: false,
        shouldPrefetch: false,
        isCartageTeamOnly: false,
        isOffwhite: true,
        isSelfTitled: false,
    },
    diagramViewer: {
        bundles: [],
        titleKey: "diagramViewer",
        homeApp: "explorer",
        islandFit: "clear",
        isRecord: true,
        shouldPrefetch: false,
        isCartageTeamOnly: false,
        isOffwhite: false,
        isSelfTitled: false,
    },
    reports: {
        bundles: ["reports"],
        titleKey: "reports",
        icon: { iconSrc: "/desktop/app-reports.svg" },
        homeApp: "reports",
        islandFit: "lane",
        isRecord: false,
        shouldPrefetch: false,
        isCartageTeamOnly: false,
        isOffwhite: false,
        isSelfTitled: false,
    },
    report: {
        bundles: ["reports"],
        titleKey: "report",
        homeApp: "reports",
        islandFit: "clear",
        isRecord: true,
        identityOf: data =>
            typeof data?.referenceNumber === "string"
                ? data.referenceNumber
                : undefined,
        shouldPrefetch: false,
        isCartageTeamOnly: false,
        isOffwhite: false,
        isSelfTitled: false,
    },
    meetWilson: {
        bundles: ["setup"],
        titleKey: "meetWilson",
        homeApp: "setup",
        islandFit: "clear",
        isRecord: false,
        shouldPrefetch: false,
        isCartageTeamOnly: false,
        isOffwhite: false,
        isSelfTitled: false,
    },
    knowledgeNote: {
        bundles: ["knowledgeFiles"],
        titleKey: "knowledgeNote",
        homeApp: "explorer",
        islandFit: "clear",
        isRecord: true,
        identityOf: data => knowledgeItemIdOf(data),
        shouldUpdateTitleOnReveal: true,
        shouldUpdateDataOnReveal: true,
        shouldPrefetch: false,
        isCartageTeamOnly: false,
        isOffwhite: false,
        isSelfTitled: false,
    },
    knowledgeDoc: {
        bundles: ["knowledgeFiles"],
        titleKey: "knowledgeDoc",
        homeApp: "explorer",
        islandFit: "lane",
        isRecord: true,
        identityOf: data => knowledgeItemIdOf(data),
        shouldUpdateTitleOnReveal: true,
        shouldUpdateDataOnReveal: true,
        shouldPrefetch: false,
        isCartageTeamOnly: false,
        isOffwhite: false,
        isSelfTitled: false,
    },
    template: {
        bundles: ["knowledgeFiles"],
        titleKey: "template",
        homeApp: "explorer",
        islandFit: "lane",
        isRecord: true,
        identityOf: data => templateIdentityOf(data),
        shouldUpdateTitleOnReveal: true,
        shouldUpdateDataOnReveal: true,
        shouldPrefetch: false,
        isCartageTeamOnly: false,
        isOffwhite: false,
        isSelfTitled: false,
    },
    warehouseScheduling: {
        bundles: ["warehouseScheduling"],
        titleKey: "warehouseScheduling",
        icon: { iconSrc: "/desktop/app-warehouse-scheduling.png" },
        homeApp: "warehouseScheduling",
        islandFit: "lane",
        isRecord: false,
        shouldPrefetch: false,
        isCartageTeamOnly: false,
        isOffwhite: false,
        isSelfTitled: false,
    },
    skill: {
        bundles: ["explorer", "settings", "document"],
        titleKey: "skill",
        homeApp: "explorer",
        islandFit: "clear",
        isRecord: true,
        identityOf: data =>
            typeof data?.slug === "string" && data.slug.length > 0
                ? data.slug
                : undefined,
        shouldUpdateDataOnReveal: true,
        transientDataKeys: ["focus"],
        shouldPrefetch: false,
        isCartageTeamOnly: false,
        isOffwhite: true,
        isSelfTitled: false,
    },
    freightDashboard: {
        bundles: ["freightDashboard", "settings"],
        titleKey: "freightDashboard",
        icon: { iconSrc: "/desktop/app-freight-dashboard.svg" },
        homeApp: "freightDashboard",
        islandFit: "clear",
        isRecord: false,
        shouldPrefetch: false,
        isCartageTeamOnly: false,
        isOffwhite: false,
        isSelfTitled: false,
    },
    cartagePay: {
        bundles: ["cartagePay", "setup"],
        titleKey: "cartagePay",
        icon: { iconSrc: "/desktop/app-wallet.svg" },
        homeApp: "cartagePay",
        islandFit: "lane",
        isRecord: false,
        shouldPrefetch: false,
        isCartageTeamOnly: false,
        isOffwhite: true,
        isSelfTitled: false,
    },
}

export const DOCK_APP_ORDER = [
    "setup",
    "explorer",
    "reports",
    "wilson",
    "comms",
    "todos",
    "warehouseScheduling",
    "freightDashboard",
    "cartagePay",
    "settings",
] as const satisfies readonly WindowType[]
